import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { HelpCircle, Send, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/15 text-yellow-400",
  answered: "bg-teal/15 text-teal",
  closed: "bg-primary-foreground/10 text-primary-foreground/40",
};

const AdminQuestions = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState<Record<string, string>>({});

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["admin-questions"],
    queryFn: async () => {
      const { data } = await supabase
        .from("technical_questions")
        .select("*, companies(name)")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: responses = [] } = useQuery({
    queryKey: ["admin-responses"],
    queryFn: async () => {
      const { data } = await supabase
        .from("question_responses")
        .select("*")
        .order("created_at", { ascending: true });
      return data ?? [];
    },
  });

  const sendReply = useMutation({
    mutationFn: async ({ questionId, content }: { questionId: string; content: string }) => {
      const { error: rErr } = await supabase.from("question_responses").insert({
        question_id: questionId,
        content: content.trim(),
        responded_by: user!.id,
      });
      if (rErr) throw rErr;
      const { error: qErr } = await supabase.from("technical_questions").update({ status: "answered" }).eq("id", questionId);
      if (qErr) throw qErr;
    },
    onSuccess: (_, { questionId }) => {
      setReplyContent((prev) => ({ ...prev, [questionId]: "" }));
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
      qc.invalidateQueries({ queryKey: ["admin-responses"] });
      qc.invalidateQueries({ queryKey: ["admin-pending-questions"] });
      toast.success("Reply sent");
    },
    onError: () => toast.error("Failed to send reply"),
  });

  return (
    <AdminLayout>
      <div className="mb-8">
        <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
        <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
          Technical <span className="accent-text">Questions</span>
        </h1>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : questions.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <HelpCircle className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No questions yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => {
            const qResponses = responses.filter((r) => r.question_id === q.id);
            const expanded = expandedId === q.id;
            const reply = replyContent[q.id] ?? "";
            return (
              <div key={q.id} className="glass-card rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedId(expanded ? null : q.id)}
                  className="w-full p-5 flex items-center gap-4 text-left hover:bg-primary-foreground/3 transition-all"
                >
                  <MessageSquare className="w-5 h-5 text-teal/50 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-primary-foreground/80 font-body font-medium truncate">{q.title}</p>
                    <p className="text-xs text-primary-foreground/30 font-body mt-0.5">
                      {(q.companies as { name: string } | null)?.name ?? "Unknown"} · {new Date(q.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-[1px] uppercase font-body ${statusColors[q.status]}`}>
                    {q.status}
                  </span>
                  {expanded ? <ChevronUp className="w-4 h-4 text-primary-foreground/30" /> : <ChevronDown className="w-4 h-4 text-primary-foreground/30" />}
                </button>

                {expanded && (
                  <div className="px-5 pb-5 border-t border-primary-foreground/5">
                    <div className="py-4">
                      <p className="text-sm text-primary-foreground/60 font-body whitespace-pre-wrap">{q.content}</p>
                    </div>

                    {qResponses.length > 0 && (
                      <div className="space-y-3 mb-4">
                        <p className="text-[11px] font-bold tracking-[2px] uppercase text-teal/50 font-body">Responses</p>
                        {qResponses.map((r) => (
                          <div key={r.id} className="p-4 bg-teal/5 rounded-xl border border-teal/10">
                            <p className="text-sm text-primary-foreground/70 font-body whitespace-pre-wrap">{r.content}</p>
                            <p className="text-xs text-primary-foreground/25 font-body mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      <p className="text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body">Add Reply</p>
                      <textarea
                        value={reply}
                        onChange={(e) => setReplyContent((prev) => ({ ...prev, [q.id]: e.target.value }))}
                        placeholder="Type your response..."
                        rows={3}
                        className="w-full px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all resize-none"
                      />
                      <button
                        onClick={() => { if (reply.trim()) sendReply.mutate({ questionId: q.id, content: reply }); }}
                        disabled={!reply.trim() || sendReply.isPending}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5" />
                        Send Reply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminQuestions;
